import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Search, FileSpreadsheet, Printer, Loader2, 
  XCircle, Calendar, BookOpen, Edit3, Trash2 
} from 'lucide-react';
import * as XLSX from 'xlsx';
import toast, { Toaster } from 'react-hot-toast';

const LedgerReport = ({ onEdit }) => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [filters, setFilters] = useState({
    date_from: '',
    type: ''
  });

  const API_BASE = "http://127.0.0.1:8000/api/ledger/";

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_BASE);
      setReportData(response.data);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Data load nahi ho paya!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Bhai, pakka delete karna hai?")) {
      try {
        await axios.delete(`${API_BASE}${id}/`);
        toast.success("Entry Deleted!");
        fetchReport();
      } catch (error) {
        toast.error("Delete fail ho gaya!");
      }
    }
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = (item.partyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (item.vehicleNo || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filters.type ? item.transactionType === filters.type : true;
    const matchesDate = filters.date_from ? item.date === filters.date_from : true;
    return matchesSearch && matchesType && matchesDate;
  });

  const exportToExcel = () => {
    setExportLoading(true);
    try {
      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");
      XLSX.writeFile(workbook, `Ledger_${new Date().toLocaleDateString()}.xlsx`);
      toast.success("Excel Downloaded!");
    } catch (err) { 
      toast.error("Export fail!"); 
    } finally { setExportLoading(false); }
  };

  return (
    <div className="report-main-wrapper">
      <Toaster position="bottom-right" />
      
      <style>{`
        .report-main-wrapper { height: 100vh; display: flex; flex-direction: column; background: #fff; font-family: sans-serif; overflow: hidden; }
        
        /* Compact Sticky Header */
        .sticky-filters { padding: 10px 15px; display: flex; gap: 10px; border-bottom: 2px solid #cbd5e1; background: #fff; align-items: flex-end; }
        .filter-item { display: flex; flex-direction: column; flex: 1; gap: 4px; }
        .filter-item label { font-size: 10px; font-weight: 800; color: #64748b; text-transform: uppercase; display: flex; align-items: center; gap: 4px; }
        .filter-item input, .filter-item select { height: 32px; padding: 0 10px; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 13px; outline: none; width: 100%; box-sizing: border-box; }
        .filter-item input:focus { border-color: #6366f1; }

        /* Table Area */
        .scroll-area { flex: 1; overflow: auto; background: #f8fafc; }
        .modern-table { width: 100%; border-collapse: separate; border-spacing: 0; table-layout: fixed; }
        .modern-table thead th { position: sticky; top: 0; background: #f8fafc; z-index: 20; padding: 12px 10px; border-bottom: 2px solid #cbd5e1; text-align: left; font-size: 11px; font-weight: 700; color: #475569; text-transform: uppercase; }
        
        .modern-table td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; background: #fff; font-size: 12.5px; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .modern-table tbody tr:hover td { background: #f1f5f9; }

        /* UI Elements */
        .v-badge { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 11px; font-weight: 700; border: 1px solid #cbd5e1; color: #334155; }
        .badge { padding: 3px 8px; border-radius: 12px; font-size: 10px; font-weight: 800; text-transform: uppercase; }
        .badge.credit { background: #dcfce7; color: #166534; }
        .badge.debit { background: #fee2e2; color: #991b1b; }
        
        .policy-box { display: flex; flex-direction: column; line-height: 1.2; }
        .policy-no { font-weight: 700; font-size: 12px; color: #1e293b; }
        .company-name { font-size: 10px; color: #64748b; }

        /* Action Buttons */
        .btn-group { display: flex; gap: 8px; }
        .btn-p { height: 32px; padding: 0 12px; background: #4f46e5; color: white; border: none; border-radius: 4px; font-size: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .btn-s { height: 32px; padding: 0 12px; background: #fff; color: #475569; border: 1px solid #cbd5e1; border-radius: 4px; font-size: 12px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; }
        .btn-icon-only { height: 32px; width: 32px; display: flex; align-items: center; justify-content: center; border: 1px solid #cbd5e1; background: #fff; border-radius: 4px; cursor: pointer; color: #64748b; }

        .text-green { color: #166534; font-weight: 700; }
        .text-red { color: #991b1b; font-weight: 700; }
        .action-icons { display: flex; gap: 10px; justify-content: center; }
        .action-icons svg { cursor: pointer; transition: 0.2s; }
        .action-icons svg:hover { transform: scale(1.2); }
      `}</style>

      {/* --- FILTER BAR --- */}
      <div className="sticky-filters no-print">
        <div className="filter-item">
          <label><Calendar size={12}/> Date</label>
          <input type="date" value={filters.date_from} onChange={(e)=>setFilters({...filters, date_from: e.target.value})} />
        </div>
        
        <div className="filter-item" style={{flex: 1.5}}>
          <label><Search size={12}/> Search Party / Vehicle</label>
          <input 
            type="text" 
            placeholder="Search typing..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} 
          />
        </div>

        <div className="filter-item">
          <label>Type</label>
          <select value={filters.type} onChange={(e)=>setFilters({...filters, type: e.target.value})}>
            <option value="">All Transactions</option>
            <option value="CREDIT">Credit (+)</option>
            <option value="DEBIT">Debit (-)</option>
          </select>
        </div>

        <div className="btn-group">
          <button className="btn-p" onClick={fetchReport}>
            {loading ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />} Search
          </button>
          <button className="btn-icon-only" onClick={() => {setSearchTerm(''); setFilters({date_from:'', type:''})}} title="Reset">
            <XCircle size={18} />
          </button>
          <button className="btn-s" style={{color: '#166534'}} onClick={exportToExcel}>
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button className="btn-s" onClick={() => window.print()}>
            <Printer size={16} /> Print
          </button>
        </div>
      </div>

      {/* --- DATA TABLE --- */}
      <div className="scroll-area">
        <table className="modern-table">
          <thead>
            <tr>
              <th width="100">Date</th>
              <th width="220">Party Name</th>
              <th width="120">Vehicle No.</th>
              <th width="100">Type</th>
              <th>Policy Details</th>
              <th width="110">Amount</th>
              <th width="110">Comm.</th>
              <th width="90">Mode</th>
              <th width="100" className="no-print" style={{textAlign:'center'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" style={{textAlign:'center', padding:'40px'}}><Loader2 className="animate-spin" size={30} color="#6366f1" /></td></tr>
            ) : filteredData.length > 0 ? (
              filteredData.map((item, index) => (
                <tr key={item.id || index}>
                  <td>{item.date}</td>
                  <td style={{fontWeight: '700', color: '#4f46e5'}}>{item.partyName}</td>
                  <td><span className="v-badge">{item.vehicleNo || 'N/A'}</span></td>
                  <td>
                    <span className={`badge ${item.transactionType?.toLowerCase()}`}>
                      {item.transactionType}
                    </span>
                  </td>
                  <td>
                    <div className="policy-box">
                      <span className="policy-no">{item.policyNo || 'N/A'}</span>
                      <span className="company-name">{item.companyName || '-'}</span>
                    </div>
                  </td>
                  <td className={item.transactionType === 'DEBIT' ? 'text-red' : 'text-green'}>
                    ₹{parseFloat(item.amount || 0).toLocaleString('en-IN')}
                  </td>
                  <td style={{color: '#b45309', fontWeight: '600'}}>
                    ₹{parseFloat(item.commissionEarned || 0).toLocaleString('en-IN')}
                  </td>
                  <td><span className="v-badge" style={{background: '#f8fafc'}}>{item.paymentMode}</span></td>
                  <td className="no-print">
                      <div className="action-icons">
                        <Edit3 size={16} color="#4f46e5" onClick={() => onEdit && onEdit(item)} />
                        <Trash2 size={16} color="#ef4444" onClick={() => handleDelete(item.id)} />
                      </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="9" style={{textAlign:'center', padding:'60px', color: '#94a3b8'}}>Koi records nahi mile... 📭</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LedgerReport;